import { useCallback } from 'react';
import { Button, Wrapper } from './styled';

export type FiltersProps = {
  filters: Array<{ text: string; value: string }>;
  messages: { allFiltersText: string };
  selectedFilters: string[];
  setSelectedFilters: (selectedFilter: string[]) => void;
  /**
   * If true, only allows one option at a time to be selected.
   */
  singleOptions?: boolean;
  /**
   * If false, hides the 'All' button
   */
  showAllButton?: boolean;
};

export function Filters({
  filters,
  selectedFilters,
  setSelectedFilters,
  messages,
  singleOptions = false,
  showAllButton = true
}: FiltersProps): JSX.Element {
  const showAll = filters.every(({ value }) =>
    selectedFilters.find((f) => f === value)
  );

  const toggleAllFilters = useCallback(() => {
    if (singleOptions) {
      setSelectedFilters(filters.map((f) => f.value));
      return;
    }
    selectedFilters?.length === 0
      ? setSelectedFilters(filters.map((f) => f.value))
      : setSelectedFilters([]);
  }, [singleOptions, selectedFilters, filters, setSelectedFilters]);

  const toggleFilter = useCallback<(filterValue: string) => void>(
    (filterValue) => {
      if (singleOptions) {
        setSelectedFilters([filterValue]);
        return;
      }
      if (selectedFilters.includes(filterValue)) {
        setSelectedFilters(
          selectedFilters.filter((value) => value !== filterValue)
        );
      } else {
        setSelectedFilters([...selectedFilters, filterValue]);
      }
    },
    [singleOptions, setSelectedFilters, selectedFilters]
  );

  return (
    <Wrapper>
      {showAllButton && (
        <Button type="button" selected={showAll} onClick={toggleAllFilters}>
          {messages.allFiltersText}
        </Button>
      )}
      {filters.map((filter) => (
        <Button
          key={filter.value}
          type="button"
          selected={selectedFilters.includes(filter.value) && !showAll}
          onClick={(): void => {
            toggleFilter(filter.value);
          }}
          data-testid={`budgets-activity-filter-${filter.value}`}
        >
          {filter.text}
        </Button>
      ))}
    </Wrapper>
  );
}
